/**
 * The #1815 onboarding-status coordinator (Tasks B+C), updated for the pre-pairing handoff
 * fix (`docs/superpowers/plans/2026-07-28-onboarding-pairing-handoff.md`, Task 2). Derives
 * which shell surface an unclaimed/claimed/unreachable node should show, so onboarding can
 * mount by default for a fresh (unclaimed) node instead of only behind the `?onboarding=1`
 * dev flag.
 *
 * Pre-pairing there is no device session yet, so the managed `HomeNodeClient` transport
 * cannot establish (its refresh path 401s before a session exists). Rather than reading
 * `claimed` through that auth-gated transport, the no-session path reads it via an injected
 * `claimedProbe` — a transport-free, public read of the home node's `claimed` signal (wired
 * in the composition root to `@econ-v1/adapters-web`'s `probeHomeNodeClaimed`, which mirrors
 * `discoverNodeByAddress`'s plain-`fetch` pattern and never throws).
 *
 * Derivation order (fail-safe: an unrecognized outcome never invents "ready" or
 * "needs-onboarding" — it falls back to `needs-pairing` or `bootstrap-required`):
 *   1. The browser already holds a valid device session → `ready` (never calls the probe).
 *   2. The probe reports the node unreachable (`{ reachable: false }`) → `bootstrap-required`
 *      (drive discovery first).
 *   3. The probe reports the node reachable and `claimed: true` (has an owner) →
 *      `needs-pairing` (additional-device pairing — the existing default behavior).
 *   4. The probe reports the node reachable and `claimed: false` (no owner yet) →
 *      `needs-onboarding`, scenario `new` (first-owner setup creates the owner).
 *   5. The probe reports the node reachable but `claimed` is not a boolean (unrecognized
 *      response shape) → `needs-pairing` (no regression — the shell still shows the pairing
 *      gate, which handles both "already paired" and "not yet paired" on its own).
 */
export class OnboardingStatusService {
    #hasActiveSession;
    #claimedProbe;
    constructor(options) {
        this.#hasActiveSession = options.hasActiveSession;
        this.#claimedProbe = options.claimedProbe;
    }
    async status() {
        if (this.#hasActiveSession())
            return { id: "onboarding-status", state: "ready" };
        const probe = await this.#claimedProbe();
        if (!probe.reachable)
            return { id: "onboarding-status", state: "bootstrap-required" };
        if (probe.claimed === undefined)
            return { id: "onboarding-status", state: "needs-pairing" };
        return probe.claimed
            ? { id: "onboarding-status", state: "needs-pairing" }
            : { id: "onboarding-status", scenario: "new", state: "needs-onboarding" };
    }
}
//# sourceMappingURL=onboarding-status-service.js.map